import React from 'react';

import LinkBox from '../../../components/Buttons/LinkBox';
import useMediaQuery from '../../../hooks/MediaQuery';
import { DEVICES } from '../../../lib/breakpoints';
import cn from '../../../lib/classNames';
import SectionHeader from '../SectionHeader/SectionHeader';

import AdvantageList from './AdvantageList/AdvantageList';
import advantage1 from './images/advantage1.jpg';
import advantage2 from './images/advantage2.jpg';
import advantage3 from './images/advantage3.jpg';
import advantage4 from './images/advantage4.jpg';
import advantage5 from './images/advantage5.jpg';
import advantage6 from './images/advantage6.jpg';
import advantage7 from './images/advantage7.jpg';
// Import mobile images
import mobileAdvantage1 from './images/mobile/advantage1.jpg';
import mobileAdvantage2 from './images/mobile/advantage2.jpg';
import mobileAdvantage3 from './images/mobile/advantage3.jpg';
import mobileAdvantage4 from './images/mobile/advantage4.jpg';
import mobileAdvantage5 from './images/mobile/advantage5.jpg';
import mobileAdvantage6 from './images/mobile/advantage6.jpg';
import mobileAdvantage7 from './images/mobile/advantage7.jpg';

import styles from './Advantage.module.scss';

type ItemProps = {
	id: string;
	title: string;
	imageSrc: string;
	description: React.ReactNode;
	list?: string[];
	learnMoreLink?: string;
	reversed?: boolean;
};

function Item({ title, imageSrc, description, list, learnMoreLink, reversed = false }: ItemProps) {
	return (
		<div className={cn(styles.item, { [styles.reversed]: reversed })}>
			<div className={styles.content}>
				<h3 className={styles.title}>{title}</h3>
				<div className={styles.description}>
					{description}
					{list && <AdvantageList items={list} />}
					{learnMoreLink && (
						<LinkBox className={styles['learn-more']} href={learnMoreLink} iconBefore="open_in_new">
							Learn more
						</LinkBox>
					)}
				</div>
			</div>
			<div className={styles.image}>
				<img src={imageSrc} alt={title} />
			</div>
		</div>
	);
}

type AdvantageType = {
	classPageContent?: string;
};

const Advantage = React.forwardRef<HTMLDivElement, AdvantageType>((props, ref) => {
	const { classPageContent } = props;
	const isMobile = useMediaQuery(DEVICES.isMobile);

	const advantageData: ItemProps[] = [
		{
			id: 'advantage1',
			title: 'The World’s First AI-generated Destination Risk Brief',
			imageSrc: isMobile ? mobileAdvantage1 : advantage1,
			description: (
				<p>
					Our AI-powered system generates comprehensive risk intelligence on demand, in minutes.
				</p>
			),
			list: [
				'Transforms complex data into clear, actionable insights',
				'Synthesizes information from top government and high-value sources',
				'Provides critical local emergency information and neighborhood safety maps',
			],
		},
		{
			id: 'advantage2',
			title: 'Granular Global Intelligence',
			imageSrc: isMobile ? mobileAdvantage2 : advantage2,
			description: (
				<p>
					Unlike generic country advisories, Safe-xplore delivers city-level risk assessments across
					thousands of US and global destinations.
				</p>
			),
			list: [
				'Neighborhood-level safety information',
				'Demographic-specific health, accidents, and crime risk analysis',
				"Comparative risk metrics relative to a traveler's home city",
			],
			reversed: true,
		},
		{
			id: 'advantage3',
			title: 'Duty of Care Made Simple',
			imageSrc: isMobile ? mobileAdvantage3 : advantage3,
			description: <p>Meet your legal obligations without overcomplicating compliance.</p>,
			list: [
				'Industry-leading risk assessments eliminate subjective judgments',
				'Comprehensive briefings demonstrate due diligence',
				'Automated documentation streamlines audit requirements',
				'Consistent risk communication reduces potential liability',
			],
		},
		{
			id: 'advantage4',
			title: 'The Violent Crime Risk Index (ViCRI) for Global Cities',
			imageSrc: isMobile ? mobileAdvantage4 : advantage4,
			description: (
				<p>
					With direct access to the ViCRi index, Safe-xplore users can rate and compare a
					destination’s serious crime risks to those of their home at hundreds of U.S. and global
					cities. The ViCRI index is the most advanced international risk rating initiative focused
					specifically on the risk of violent crime victimization risks for residents and visitors
					of major metropolitan areas across the globe.
				</p>
			),
			learnMoreLink: 'https://vimeo.com/928504867/431c853dbd?share=copy',
			reversed: true,
		},
		{
			id: 'advantage5',
			title: 'Data-Driven Risk Intelligence',
			imageSrc: isMobile ? mobileAdvantage5 : advantage5,
			description: (
				<p>
					Our proprietary models translate complex crime and safety data into clear, actionable
					intelligence.
				</p>
			),
			list: [
				'Precise risk assessment based on statistical models, not opinions',
				'Traveler-specific insights tailored to demographic profiles',
				'Simple relative risk comparisons to home cities for better understanding',
				'Clear recommendations that drive appropriate precautions',
			],
		},
		{
			id: 'advantage6',
			title: 'Actionable Safety Intelligence',
			imageSrc: isMobile ? mobileAdvantage6 : advantage6,
			description: (
				<p>
					Safe-xplore doesn&apos;t just identify dangers – it provides specific guidance on how to
					mitigate them.
				</p>
			),
			list: [
				'Clear safety recommendations tailored to each destination',
				'Practical advice that addresses actual risks, not stereotypes',
				'Location-specific guidance for emergency situations',
			],
			reversed: true,
		},
		{
			id: 'advantage7',
			title: 'Custom payment plan & comfortable team management',
			imageSrc: isMobile ? mobileAdvantage7 : advantage7,
			description: (
				<p>
					Make sure that your travelling team members are safe and secured in personal risk decision
					making, giving them access to generate reports, monitoring their report history and
					checking if they have relevant information about their travel destination.
				</p>
			),
			list: [
				'Admin access to the report history of your team',
				'Sign up via company domain',
				'Add new company members',
			],
		},
	];

	return (
		<div ref={ref} className={cn(styles.wrap, classPageContent)}>
			<SectionHeader
				title="The Safe-xplore Advantage"
				subTitle="We provide up-to-date, verified health, safety, and security information along with practical recommendations to business and leisure travelers."
				className={styles.title}
			/>
			<div className={styles.items}>
				{advantageData.map((item) => (
					<Item key={item.id} {...item} />
				))}
			</div>
		</div>
	);
});

Advantage.displayName = 'Advantage';

export default Advantage;
